import { UserIcon, WarningDiamondIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { Link } from 'react-router-dom'
import { z } from 'zod'

import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { SortOrderSchema, TableColumn } from '@/components/ui/table/table.types'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { routerFactory } from '@/factories'
import { getAssetLink } from '@/helpers/get-asset-link'
import { MaintenanceRequest } from '@/models/features'
import * as S from '@/pages/maintenance/maintenance-dashboard/maintenance-dashboard.styled'
import {
  getMaintenanceStatusColor,
  getMaintenanceStatusLabel,
  MaintenanceStatusFilterEnum,
} from '@/pages/maintenance/maintenance-dashboard/maintenance-dashboard.utils'
import { formatDateOnly } from '@/utils/date'

export const UNIT_MAINTENANCE_TABLE_ID = 'unit-maintenance'

export const unitMaintenanceFiltersSchema = z.object({
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  // Note: falls back to "all" for anything else, same as the maintenance dashboard.
  status: MaintenanceStatusFilterEnum.catch('all').optional(),
  toDate: z.coerce.number().optional(),
})

export type UnitMaintenanceFilters = z.infer<typeof unitMaintenanceFiltersSchema>

export const unitMaintenanceInitialFilters: UnitMaintenanceFilters = {
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  skip: 0,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  status: 'all',
  toDate: undefined,
}

interface GetUnitMaintenanceColumnsProps {
  t: TFunction<['maintenance']>
  timezone: string
}

export const getUnitMaintenanceColumns = ({
  t,
  timezone,
}: GetUnitMaintenanceColumnsProps): TableColumn<MaintenanceRequest>[] => [
  {
    id: 'idNumber',
    render: (request) => request.id,
    title: t('maintenance:requests.columns.idNumber'),
    width: '12rem',
  },
  {
    id: 'submittedBy',
    render: ({ clientId: client, priority, shortDescription }) => {
      if (!client) return shortDescription || EMPTY_VALUE

      return (
        <S.CellWithAvatar>
          <Avatar
            size="small"
            src={getAssetLink({
              fallback: { file: 'avatar.png' },
              file: client.avatar ?? undefined,
              path: 'chitown-avatar/avatar',
            })}
            alt={`${client.firstName} ${client.lastName}`}
            fallbackIcon={UserIcon}
          />
          <S.CellTextWrapper>
            <Typography as="span" size="text-small" weight="medium" color="black800" breakLines={1}>
              <Link
                to={routerFactory.getPathString({
                  params: { clientId: client.id },
                  path: '/dashboard/clients/:clientId',
                })}
                style={{ color: 'inherit', textDecoration: 'underline' }}
                onClick={(event) => event.stopPropagation()}
              >
                {`${client.firstName} ${client.lastName}`}
              </Link>
            </Typography>
            <Typography as="span" size="text-small" color="black400" breakLines={1}>
              {client.email}
            </Typography>
          </S.CellTextWrapper>
          {priority === 'Emergency' && (
            <Tooltip
              trigger={
                <S.UrgentBadge>
                  <Icon icon={WarningDiamondIcon} size="1.4rem" color="white" weight="fill" />
                </S.UrgentBadge>
              }
            >
              {t('maintenance:requests.cells.emergencyRequest')}
            </Tooltip>
          )}
        </S.CellWithAvatar>
      )
    },
    title: t('maintenance:requests.columns.submittedBy'),
    width: '22rem',
  },
  {
    id: 'scheduledDate',
    render: (request) => formatDateOnly(request.scheduledDate),
    title: t('maintenance:requests.columns.scheduledDate'),
    width: '16rem',
  },
  {
    id: 'assignee',
    render: ({ assignee }) =>
      assignee ? (
        <S.CellWithAvatar>
          <Avatar
            size="small"
            src={getAssetLink({
              fallback: { file: 'avatar.png' },
              file: assignee.user.avatar ?? undefined,
              path: 'chitown-avatar/avatar',
            })}
            alt={`${assignee.user.firstName} ${assignee.user.lastName}`}
            fallbackIcon={UserIcon}
          />
          <S.CellTextWrapper>
            <Typography as="span" size="text-small" color="black800" breakLines={1}>
              {`${assignee.user.firstName} ${assignee.user.lastName}`}
            </Typography>
            <Typography as="span" size="text-small" color="black400" breakLines={1}>
              {assignee.user.email}
            </Typography>
          </S.CellTextWrapper>
        </S.CellWithAvatar>
      ) : (
        EMPTY_VALUE
      ),
    title: t('maintenance:requests.columns.assignee'),
    width: '20rem',
  },
  {
    id: 'status',
    render: (request) => (
      <Badge color={getMaintenanceStatusColor(request.status)} variant="light" size="small">
        {getMaintenanceStatusLabel(request.status, t)}
      </Badge>
    ),
    title: t('maintenance:requests.columns.status'),
    width: '14rem',
  },
  {
    id: 'updatedOn',
    render: (request) => formatDateOnly(toZonedTime(request.updatedAt, timezone)),
    sortKey: 'updatedAt',
    title: t('maintenance:requests.columns.updatedOn'),
    width: '12rem',
  },
  {
    id: 'description',
    render: (request) => request.shortDescription || EMPTY_VALUE,
    title: t('maintenance:requests.columns.description'),
    width: '24rem',
  },
]
